<template>
  <div class="d-flex flex-column align-items-center">
    <content-body-card class="w-100 p-0">
      <el-row class="mb-20">
        <el-col :span="24">
          <!-- Member selection -->
          <div class="filters-container mb-20" v-if="breakpoint !== 'mobile'">
            <el-row :gutter="20">
              <el-col :span="8">
                <el-select v-model="selectedMember" placeholder="Select member" class="w-100">
                  <el-option v-for="member in members" :key="member._id" :label="`${member.profile.firstName} ${member.profile.lastName}`" :value="member._id">
                {{ member.profile.firstName }} {{ member.profile.lastName }}
              </el-option>
            </el-select>
              </el-col>
              <el-col :span="8">
                <el-input
                  placeholder="Search by team name..."
                  v-model="searchQuery"
                  prefix-icon="el-icon-search"
                  clearable
                  class="w-100"
                ></el-input>
              </el-col>
              <el-col :span="8">
                <el-select v-model="filters.paymentStatus" placeholder="Payment Status" clearable class="w-100">
                  <el-option label="Paid" value="paid"></el-option>
                  <el-option label="Unpaid" value="unpaid"></el-option>
                </el-select>
              </el-col>
            </el-row>
          </div>

          <!-- Sort controls -->
          <div class="sort-container mb-20">
            <el-row>
              <el-col :span="24" class="d-flex justify-content-between align-items-center">
                <!-- View mode toggle -->
                <div v-if="breakpoint !== 'mobile'">
                  <el-radio-group v-model="viewMode" size="small">
                    <el-radio-button label="card">
                      <i class="el-icon-s-grid"></i> Card
                    </el-radio-button>
                    <el-radio-button label="list">
                      <i class="el-icon-s-order"></i> List
                    </el-radio-button>
                  </el-radio-group>
                </div>

                <!-- Sort dropdown -->
                <div class="sort-wrapper" v-if="breakpoint !== 'mobile'">
                  <span class="sort-label mr-10">Sort by:</span>
                  <el-select v-model="sortBy" placeholder="Sort by" size="small" class="sort-select">
                    <el-option label="None" value=""></el-option>
                    <el-option label="Team Name (A-Z)" value="team-asc"></el-option>
                    <el-option label="Team Name (Z-A)" value="team-desc"></el-option>
                    <el-option label="Registration Date (Newest)" value="reg-desc"></el-option>
                    <el-option label="Registration Date (Oldest)" value="reg-asc"></el-option>
                  </el-select>
                </div>
              </el-col>
            </el-row>
          </div>

          <!-- Card-based layout for desktop when in card view mode -->
          <div v-if="viewMode === 'card' && breakpoint !== 'mobile'">
            <!-- Season group header -->
            <div v-for="seasonGroup in registrationsBySeason" :key="seasonGroup.season" class="season-group">
              <div class="season-header" @click="toggleSeason(seasonGroup.season)">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="season-title">
                    <i :class="`el-icon-arrow-${seasonGroup.isExpanded ? 'down' : 'right'}`"></i>
                    Season: {{ seasonGroup.season }}
                  </div>
                  <div class="season-count">
                    {{ seasonGroup.registrations.length }} teams
                  </div>
                </div>
              </div>

              <div v-if="seasonGroup.isExpanded" class="season-content card-grid">
                <el-card
                  v-for="registration in seasonGroup.registrations"
                  :key="registration._id"
                  class="registration-card mb-15"
                  :data-entity-type="registration.entityType"
                  shadow="hover"
                >
                  <div slot="header" class="card-header w-100">
                    <div class="d-flex justify-content-between align-items-start w-100">
                      <div class="d-flex flex-column align-items-start w-100">
                        <div class="entity-name">{{ eventNameFormatter(registration) }}</div>
                        <div class="team-name-text">{{ registration.team?.name || 'Team Name Not Available' }}</div>
                      </div>
                      <div class="d-flex align-items-center">
                        <div class="payment-badge" :class="isPaid(registration) ? 'paid' : 'unpaid'">
                          {{ isPaid(registration) ? 'Paid' : 'Unpaid' }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="card-content">
                    <div class="card-body-row">
                      <div class="info-section">
                        <div class="unified-info-grid">
                          <div class="info-col text-center">
                            <div class="info-label"><i class="el-icon-key"></i> Team Code</div>
                            <div class="info-value">
                              <template v-if="get(registration, 'team.teamCode')">
                                {{ get(registration, 'team.teamCode') }}
                              </template>
                              <template v-else>
                                <el-button size="mini" type="text" @click="generateTeamCode(registration)" class="generate-code-btn">
                                  Generate Code
                                </el-button>
                              </template>
                            </div>
                          </div>

                          <div class="info-col text-center">
                            <div class="info-label"><i class="el-icon-date"></i> Registered On</div>
                            <div class="info-value">{{ registration.createdAt | formatDate }}</div>
                          </div>

                          <div class="info-col text-center">
                            <div class="info-label"><i class="el-icon-wallet"></i> Payment</div>
                            <div class="info-value">{{ getPaidStatus(registration) }}</div>
                          </div>

                          <!-- Show More button with centered arrow icon directly on unified grid border -->
                          <div class="expand-button-container">
                            <el-button
                              type="text"
                              class="expand-button-icon"
                              size="mini"
                              @click="toggleExpand(registration)"
                            >
                              <i :class="`el-icon-arrow-${registration.expanded ? 'up' : 'down'} arrow-icon`"></i>
                            </el-button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Expanded content -->
                    <transition name="expand">
                      <div v-if="registration.expanded" class="expanded-content">
                        <el-divider content-position="left">Additional Information</el-divider>

                        <!-- Status information -->
                        <div class="info-section">
                          <h4>Status Information</h4>
                          <div class="status-details">
                            <div class="status-item">
                              <span class="status-label">Activity Status:</span>
                              <el-tag size="mini" effect="plain" :type="registration.expired ? 'danger' : 'success'" class="status-tag">
                                {{ registration.expired ? 'Inactive' : 'Active' }}
                              </el-tag>
                            </div>
                          </div>
                        </div>

                        <!-- Payment details -->
                        <div v-if="!isPaid(registration)" class="info-section">
                          <h4>Payment Details</h4>
                          <div class="payment-info">
                            <span class="payment-label">Amount Paid:</span>
                            <span class="payment-value">{{ getPaidStatus(registration) }}</span>
                          </div>
                        </div>

                        <!-- Product information -->
                        <div v-if="registration.productType !== 'legacy-membership'" class="info-section">
                          <h4>Product Information</h4>
                          <div class="info-detail">
                            <strong>Product Type:</strong> {{registration.productType === 'indv' ? 'Individual'
                                    :registration.productType === 'indv-to-team' ? 'Individual To Team'
                                    :registration.productType === 'team' ? 'Team'
                                  : ''
                                  }}
                </div>
                          <div v-if="registration.tags && registration.tags.length > 0" class="info-detail">
                            <strong>Tags:</strong> {{ joinTag(registration.tags) }}
                  </div>
                </div>

                        <!-- Results section -->
                        <div v-if="Object.keys(registration.results).length" class="info-section">
                          <h4>Results</h4>
                          <div class="info-detail">
                            <strong>{{ registration.results.name }}:</strong> {{ registration.results.value }} ({{ getResultLabel(registration.results.type) }})
                          </div>
                        </div>

                        <!-- Files section -->
                        <div class="info-section">
                          <h4>Files Uploaded</h4>
                          <div v-for="([group, files]) in groupedUploads(registration.files)" :key="group" class="file-group">
                            <h5>{{ group }}</h5>
                    <el-upload
                      :auto-upload="false"
                              :ref="`uploadDesktop-${registration._id}-${group}`"
                      :file-list="files ? files.map(file => ({_id: file._id, name: `${file.originalName || file.fileName} Uploaded At: ${dateFormatter(file.createdAt)}`, url: file.path, registrationId: file.meta.registrationId, type: file.fileType})) : []"
                      :on-preview="previewUpload"
                      :on-change="(file, fileList) => handleChange(file, fileList, {
                        _id: files._id,
                                registrationId: registration._id,
                        additionalQuestion: group,
                                entityId: registration.entityId,
                                entityType: registration.entityType,
                        memberId: value._id,
                        type: 'additional-question',
                      })"
                      :on-error="onError"
                      :before-upload="beforeUpload"
                      :before-remove="removeFile"
                      action=""
                      :headers="uploadHeaders"
                      :accept="accept"
                              :disabled="getUploadExpiry(registration)"
                      >
                      <el-button
                      slot="trigger"
                      size="small"
                      type="primary"
                                v-if="isNotExpired(registration)"
                      >
                        Add File(s)
                      </el-button>
                    </el-upload>
                  </div>
                </div>

                        <!-- TeamType specific information (BASEBALL) -->
                        <div v-if="nationalId === 4 && registration.productType == 'indv-to-team'">
                          <fieldset class="mt-10" v-if="registration.team.meta && registration.team.meta.captain" style="width: 450px">
                    <legend>Captain</legend>
                            <p>Captain: {{ registration.team.meta.captain.name || '-' }}</p>
                            <p>Email: {{ registration.team.meta.captain.email || '-' }}</p>
                            <p>Mobile: {{ registration.team.meta.captain.mobile || '-' }}</p>
                  </fieldset>
                          <fieldset class="mt-10" v-if="registration.team.meta && registration.team.meta.viceCaptain" style="width: 450px">
                    <legend>Vice Captain</legend>
                            <p>Vice Captain: {{ registration.team.meta.viceCaptain.name || '-' }}</p>
                            <p>Email: {{ registration.team.meta.viceCaptain.email || '-' }}</p>
                            <p>Mobile: {{ registration.team.meta.viceCaptain.mobile || '-' }}</p>
                  </fieldset>
                </div>
                      </div>
                    </transition>

                    <!-- Action section with buttons only -->
                    <div class="action-section d-flex justify-content-end align-items-center">
                      <!-- Payment buttons -->
                      <div class="payment-action-buttons" v-if="!isPaid(registration)">
                        <el-button
                          type="danger"
                          size="mini"
                          class="pay-button"
                          @click="triggerPaymentDialog(registration)"
                        >
                          Pay Now
                </el-button>
                      </div>

                      <!-- Action buttons without grouping -->
                      <div class="action-buttons">
                        <el-tooltip content="Download Receipt" placement="top">
                          <el-button type="primary" size="mini" icon="el-icon-download" @click="downloadReceipt(registration)"></el-button>
                        </el-tooltip>

                        <el-tooltip content="Update Questions" placement="top">
                          <el-button type="primary" size="mini" icon="el-icon-edit-outline" @click="showAQDialog(registration)"></el-button>
                        </el-tooltip>

                        <el-tooltip content="Download Member List" placement="top">
                          <el-button type="primary" size="mini" icon="el-icon-document" @click="downloadMemberList(registration)"></el-button>
                        </el-tooltip>
                      </div>
                    </div>

                    <!-- Show upgrade button only when needed -->
                    <div class="secondary-actions d-flex justify-content-end" v-if="isUpgradeEligible(registration)">
                      <template v-if="isUpgradeEligible(registration)">
                        <el-button type="primary" size="mini" @click="handleDropdown(registration,'open')" class="ml-5">
                          Upgrade
                        </el-button>
                      </template>
                    </div>

                    <!-- Upgrade dropdown section -->
                    <div v-if="registration.entityType === 'membership' && get(registration, 'regoEntity.products.isUpgradeable') && registration.upgradingDropdown" class="upgrade-dropdown mt-10">
                      <el-button class="mb-05" size="mini" type="danger" @click="handleDropdown(registration, 'close')">
                  Cancel
                </el-button>
                <el-cascader
                        v-model="registration.regoEntity.membershipToUpgrade"
                        :options="getFilterdMemberships(get(registration, 'regoEntity.products.upgradeableMembershipList'))"
                :props="{label: 'name', value: '_id', children: 'products' }"
                clearable
                filterable
                        size="small"
                :show-all-levels="true"
                separator=" -> "
                        @change="handleChangeMem(registration)"
                        class="w-100"
                      >
                        <template slot-scope="{ data }">
                          <div style="display: flex; justify-content: space-between; align-items: center; width: 100%">
                            <span v-if="data.products">{{ data.name }}</span>
                            <template v-else>
                              <span>{{ data.type === 'indv' ? 'Individual' : data.type === 'team' ? 'Team' : data.type === 'family' ? 'Family' : '' }}</span>
                              <el-tag size="mini" style="margin-left: 5px">{{ capitalize(data.memberType) }}</el-tag>
              </template>
                  </div>
              </template>
                      </el-cascader>
                </div>
                </div>
                </el-card>
               </div>
            </div>
          </div>

          <!-- List-based layout for desktop when in list view mode -->
          <div v-if="viewMode === 'list' && breakpoint !== 'mobile'" class="list-view">
            <div v-for="seasonGroup in registrationsBySeason" :key="seasonGroup.season" class="season-group">
              <!-- Season header just like in card view -->
              <div class="season-header" @click="toggleSeason(seasonGroup.season)">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="season-title">
                    <i :class="`el-icon-arrow-${seasonGroup.isExpanded ? 'down' : 'right'}`"></i>
                    Season: {{ seasonGroup.season }}
                  </div>
                  <div class="season-count">
                    {{ seasonGroup.registrations.length }} teams
                  </div>
                </div>
              </div>

              <!-- List content -->
              <div v-if="seasonGroup.isExpanded" class="list-content">
          <el-table
                  :data="seasonGroup.registrations"
                  style="width: 100%"
                  row-key="_id"
                  :row-class-name="getRowClass"
            border
                  :cell-style="{textAlign:'center'}"
                  :header-row-style="{height: '48px'}"
                  :row-style="{height: '48px'}"
                  class="clean-table"
                >
                  <!-- Expand column as first column -->
                  <el-table-column type="expand" width="60">
              <template slot-scope="scope">
                      <div class="table-expand-content">
                        <el-tabs type="border-card" class="expanded-tabs">
                          <el-tab-pane label="Details">
                            <div class="expanded-details">
                              <!-- Status information -->
                              <div class="info-section">
                                <h4>Status Information</h4>
                                <div class="status-details">
                                  <div class="status-item">
                                    <span class="status-label">Activity Status:</span>
                                    <el-tag size="mini" effect="plain" :type="scope.row.expired ? 'danger' : 'success'" class="status-tag">
                                      {{ scope.row.expired ? 'Inactive' : 'Active' }}
                                    </el-tag>
                  </div>
                </div>
                              </div>

                              <!-- Product information -->
                              <div v-if="scope.row.productType !== 'legacy-membership'" class="info-section">
                                <h4>Product Information</h4>
                                <div class="info-detail">
                                  <strong>Product Type:</strong> {{scope.row.productType === 'indv' ? 'Individual'
                                          :scope.row.productType === 'indv-to-team' ? 'Individual To Team'
                                          :scope.row.productType === 'team' ? 'Team'
                                          : ''
                                          }}
                                </div>
                                <div v-if="scope.row.tags && scope.row.tags.length > 0" class="info-detail">
                                  <strong>Tags:</strong> {{ joinTag(scope.row.tags) }}
                                </div>
                              </div>

                              <!-- Results section -->
                              <div v-if="Object.keys(scope.row.results).length" class="info-section">
                                <h4>Results</h4>
                                <div class="info-detail">
                                  <strong>{{ scope.row.results.name }}:</strong> {{ scope.row.results.value }} ({{ getResultLabel(scope.row.results.type) }})
                                </div>
                              </div>

                              <!-- TeamType specific information (BASEBALL) -->
                              <div v-if="nationalId === 4 && scope.row.productType == 'indv-to-team'" class="info-section">
                                <h4>Team Leadership</h4>
                                <div v-if="scope.row.team.meta && scope.row.team.meta.captain" class="captain-info">
                                  <h5>Captain</h5>
                                  <div class="info-detail"><strong>Name:</strong> {{ scope.row.team.meta.captain.name || '-' }}</div>
                                  <div class="info-detail"><strong>Email:</strong> {{ scope.row.team.meta.captain.email || '-' }}</div>
                                  <div class="info-detail"><strong>Mobile:</strong> {{ scope.row.team.meta.captain.mobile || '-' }}</div>
                                </div>
                                <div v-if="scope.row.team.meta && scope.row.team.meta.viceCaptain" class="vice-captain-info">
                                  <h5>Vice Captain</h5>
                                  <div class="info-detail"><strong>Name:</strong> {{ scope.row.team.meta.viceCaptain.name || '-' }}</div>
                                  <div class="info-detail"><strong>Email:</strong> {{ scope.row.team.meta.viceCaptain.email || '-' }}</div>
                                  <div class="info-detail"><strong>Mobile:</strong> {{ scope.row.team.meta.viceCaptain.mobile || '-' }}</div>
                                </div>
                              </div>
                            </div>
                          </el-tab-pane>

                          <el-tab-pane label="Files" v-if="scope.row.files && scope.row.files.length > 0">
                            <div class="file-list">
                              <div v-for="([group, files]) in groupedUploads(scope.row.files)" :key="group" class="file-group">
                <h5>{{ group }}</h5>
                <el-upload
                                  :auto-upload="false"
                                  :ref="`uploadList-${scope.row._id}-${group}`"
                                  :file-list="files ? files.map(file => ({_id: file._id, name: `${file.originalName || file.fileName} Uploaded At: ${dateFormatter(file.createdAt)}`, url: file.path, registrationId: file.meta.registrationId, type: file.fileType})) : []"
                  :on-preview="previewUpload"
                  :on-change="(file, fileList) => handleChange(file, fileList, {
                    _id: files._id,
                    registrationId: scope.row._id,
                    additionalQuestion: group,
                    entityId: scope.row.entityId,
                    entityType: scope.row.entityType,
                    memberId: value._id,
                    type: 'additional-question',
                  })"
                  :on-error="onError"
                                  :before-upload="beforeUpload"
                  :before-remove="removeFile"
                  action=""
                  :headers="uploadHeaders"
                  :accept="accept"
                  :disabled="getUploadExpiry(scope.row)"
                  >
                  <el-button
                  slot="trigger"
                  size="small"
                  type="primary"
                  v-if="isNotExpired(scope.row)"
                  >
                    Add File(s)
                  </el-button>
                </el-upload>
              </div>
                </div>
                          </el-tab-pane>
                        </el-tabs>

                        <!-- Action section for payment buttons -->
                        <div class="expanded-actions" v-if="!isPaid(scope.row)">
                          <el-button
                            type="danger"
                            size="small"
                            @click="triggerPaymentDialog(scope.row)"
                          >
                            Pay Now
                  </el-button>
                </div>
                  </div>
              </template>
                  </el-table-column>

                  <!-- Team Name column -->
                  <el-table-column prop="team.name" label="Team Name" min-width="150" align="left">
              <template slot-scope="scope">
                      <div class="entity-cell">
                        <div class="entity-name">{{ scope.row.team?.name || '-' }}</div>
                        <div v-if="get(scope.row, 'team.teamCode')" class="team-code-list text-muted">
                          Team Code: {{ get(scope.row, 'team.teamCode') }}
                </div>
                        <div v-else class="team-code-generate">
                          <el-button size="mini" type="text" @click="generateTeamCode(scope.row)">
                            Generate Code
                  </el-button>
                        </div>
                </div>
              </template>
            </el-table-column>

                  <!-- Event Name column -->
                  <el-table-column label="Event Name" min-width="150" align="left">
                    <template slot-scope="scope">
                      <div class="event-cell">
                        <div class="event-name">{{ eventNameFormatter(scope.row) }}</div>
                      </div>
                    </template>
            </el-table-column>

                  <!-- Registration Date column -->
                  <el-table-column label="Registered" width="110" align="center">
              <template slot-scope="scope">
                  {{ scope.row.createdAt | formatDate }}
              </template>
            </el-table-column>

                  <!-- Registered By column -->
                  <el-table-column label="Registered By" width="140" align="center">
                    <template slot-scope="scope">
                      {{ getValue(scope.row, 'member.profile.firstName') }} {{ getValue(scope.row, 'member.profile.lastName') }}
                    </template>
            </el-table-column>

                  <!-- Payment column -->
                  <el-table-column label="Payment" width="180" align="center">
              <template slot-scope="scope">
                      <div class="payment-column">
                        <div class="payment-status">
                          <template v-if="isPaid(scope.row)">
                            <el-tag type="success" size="mini" effect="plain">Paid</el-tag>
                          </template>
                          <template v-else>
                            {{ getPaidStatus(scope.row) }}
                          </template>
                        </div>
                        <el-button
                          v-if="!isPaid(scope.row)"
                          type="danger"
                          size="mini"
                          class="pay-button-list mt-5"
                          @click="triggerPaymentDialog(scope.row)"
                        >
                          Pay Now
                  </el-button>
                      </div>
                    </template>
                  </el-table-column>

                  <!-- Actions column -->
                  <el-table-column width="60" label="" class-name="actions-column">
                    <template slot-scope="scope">
                      <el-dropdown trigger="click" size="small" @command="handleCommand($event, scope.row)">
                        <span class="el-dropdown-link">
                          <i class="el-icon-more"></i>
                        </span>
                  <el-dropdown-menu slot="dropdown">
                          <el-dropdown-item command="download">Download Receipt</el-dropdown-item>
                          <el-dropdown-item command="questions">Additional Questions</el-dropdown-item>
                          <el-dropdown-item command="members">Member List</el-dropdown-item>
                        </el-dropdown-menu>
                      </el-dropdown>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </div>
          </div>

          <!-- Mobile view -->
          <div v-if="breakpoint === 'mobile'">
            <!-- Mobile filter controls -->
            <div class="filters-container mb-10">
              <el-row :gutter="10">
                <el-col :span="24">
                  <el-select v-model="mobileFilters.paymentStatus" placeholder="Payment Status" clearable class="w-100">
                    <el-option label="Paid" value="paid"></el-option>
                    <el-option label="Unpaid" value="unpaid"></el-option>
                  </el-select>
                </el-col>
              </el-row>
            </div>

            <!-- Mobile card list -->
            <div v-for="registration in filteredMobileRegistrations" :key="registration.id" class="mb-4">
              <el-card class="box-card mb-10 br-05 highlight-card" shadow="always">
                <div slot="header" class="header-container">
                  <div class="d-flex justify-content-between align-items-start">
                    <span class="entity-name">{{ entityFormatter(registration) }}</span>
                    <div class="actions-container d-flex align-items-center gap-10">
                      <div class="payment-badge" :class="isPaid(registration) ? 'paid' : 'unpaid'">
                        {{ isPaid(registration) ? 'Paid' : 'Unpaid' }}
                      </div>
                      <el-dropdown class="more-actions-dropdown" trigger="click">
                        <i class="el-icon-more"></i>
                        <el-dropdown-menu slot="dropdown">
                          <el-dropdown-item icon="el-icon-download" @click.native="downloadReceipt(registration)">
                            Download Receipt
                    </el-dropdown-item>
                          <el-dropdown-item icon="el-icon-edit-outline" @click.native="showAQDialog(registration)">
                            Update Questions
                    </el-dropdown-item>
                          <el-dropdown-item icon="el-icon-document" @click.native="downloadMemberList(registration)">
                            Download Members
                    </el-dropdown-item>
                          <el-dropdown-item v-if="!isPaid(registration)" icon="el-icon-money" @click.native="triggerPaymentDialog(registration)">
                            Make Payment
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
                    </div>
                  </div>
                </div>

                <div class="d-grid grid-cols-2">
                  <div class="grid-item">
                    <div class="mb-05">
                      <span class="entity-type-mobile">
                        {{ registration.entityType | cap }}
                      </span>
                    </div>
                    <div class="mb-05">
                      <i class="el-icon-user"></i>
                      Type: {{ registration.memberType | cap }}
                    </div>
                    <div class="">
                      <i class="el-icon-date"></i>
                      Reg.: {{ registration.createdAt | formatDate }}
                    </div>
                  </div>
                  <div class="grid-item text-right">
                    <div class="mb-05">
                      <div v-if="get(registration, 'team.teamCode')" class="team-code-mobile">
                        <i class="el-icon-key"></i> {{ get(registration, 'team.teamCode') }}
                      </div>
                      <div v-else>
                        <el-button size="mini" type="text" @click="generateTeamCode(registration)">
                          Generate Code
                        </el-button>
                      </div>
                    </div>
                    <div class="mb-05">
                      <i class="el-icon-s-order"></i>
                      Season: {{ registration.season }}
                    </div>
                    <div class="grid-item">
                      <i class="el-icon-wallet"></i>
                      Payment: {{ getPaidStatus(registration) }}
                    </div>
                  </div>
                </div>

                <transition
                  @before-enter="beforeEnter"
                  @enter="enter"
                  @before-leave="beforeLeave"
                  @leave="leave"
                  name="expand">
                  <div v-if="registration.expanded" class="expandable-content mt-10">
                    <!-- Product info -->
                    <div v-if="registration.productType !== 'legacy-membership'">
                      <strong>Product Type:</strong> {{registration.productType === 'indv' ? 'Individual'
                              :registration.productType === 'indv-to-team' ? 'Individual To Team'
                              :registration.productType === 'team' ? 'Team'
                              : ''
                      }}
                    </div>
                    <div v-if="registration.tags && registration.tags.length > 0" class="mt-05">
                      <strong>Tags:</strong> {{ joinTag(registration.tags) }}
                    </div>
                    <div class="mt-05">
                      <strong>Status:</strong> {{ registration.expired ? 'Inactive' : 'Active' }}
                    </div>

                    <!-- Results -->
                    <div class="mt-05">
                      <span><strong>Result:</strong> </span>
                      <div v-if="Object.keys(registration.results).length">
                        <div>{{ registration.results.name }}</div>
                        <div class="font-light">{{ registration.results.value }} ({{ getResultLabel(registration.results.type) }})</div>
                      </div>
                      <span v-else>Not Released Yet</span>
                    </div>

                    <!-- Upgrade section -->
                    <div class="mt-05" v-if="isUpgradeEligible(registration)">
                      <el-button size="small" type="primary" @click="handleDropdown(registration,'open')">
                        Upgrade Membership
                      </el-button>

                      <div v-if="registration.entityType === 'membership' && get(registration, 'regoEntity.products.isUpgradeable') && registration.upgradingDropdown" class="mt-05">
                        <el-button class="mb-05" size="small" type="danger" @click="handleDropdown(registration, 'close')">
                          Cancel
                        </el-button>
                        <el-cascader
                          v-model="registration.regoEntity.membershipToUpgrade"
                          :options="getFilterdMemberships(get(registration, 'regoEntity.products.upgradeableMembershipList'))"
                          :props="{label: 'name', value: '_id', children: 'products' }"
                          clearable
                          filterable
                          size="small"
                          :show-all-levels="true"
                          separator=" -> "
                          @change="handleChangeMem(registration)"
                          class="w-100"
                        ></el-cascader>
                      </div>
                    </div>

                    <!-- Files upload -->
                    <div>
                      <div class="mt-10"><strong>Files Uploaded:</strong></div>
                      <div v-for="([group, files]) in groupedUploads(registration.files)" :key="group">
                        <h5 class="mt-05 mb-05">{{ group }}</h5>
                        <el-upload
                          :auto-upload="false"
                          :ref="`uploadMobile-${registration._id}-${group}`"
                          :file-list="files ? files.map(file => ({_id: file._id, name: `${file.originalName || file.fileName} Uploaded At: ${dateFormatter(file.createdAt)}`, url: file.path, registrationId: file.meta.registrationId, type: file.fileType})) : []"
                          :on-preview="previewUpload"
                          :on-change="(file, fileList) => handleChange(file, fileList, {
                            _id: files._id,
                            registrationId: registration._id,
                            additionalQuestion: group,
                            entityId: registration.entityId,
                            entityType: registration.entityType,
                            memberId: value._id,
                            type: 'additional-question',
                          })"
                          :on-error="onError"
                          :before-upload="beforeUpload"
                          :before-remove="removeFile"
                          action=""
                          :headers="uploadHeaders"
                          :accept="accept"
                          :disabled="getUploadExpiry(registration)"
                        >
                          <el-button
                            slot="trigger"
                            size="small"
                            type="primary"
                            v-if="isNotExpired(registration)"
                          >
                            Add File(s)
                          </el-button>
                        </el-upload>
                      </div>
                    </div>

                    <!-- Team specific info for baseball -->
                    <div v-if="nationalId === 4 && registration.productType == 'indv-to-team'">
                      <div v-if="registration.team.meta && registration.team.meta.captain" class="mt-10">
                        <h5>Captain</h5>
                        <p>Captain: {{ registration.team.meta.captain.name || '-' }}</p>
                        <p>Email: {{ registration.team.meta.captain.email || '-' }}</p>
                        <p>Mobile: {{ registration.team.meta.captain.mobile || '-' }}</p>
                      </div>
                      <div v-if="registration.team.meta && registration.team.meta.viceCaptain" class="mt-10">
                        <h5>Vice Captain</h5>
                        <p>Vice Captain: {{ registration.team.meta.viceCaptain.name || '-' }}</p>
                        <p>Email: {{ registration.team.meta.viceCaptain.email || '-' }}</p>
                        <p>Mobile: {{ registration.team.meta.viceCaptain.mobile || '-' }}</p>
                      </div>
                    </div>

                    <!-- Payment button -->
                    <div v-if="!isPaid(registration)" class="mt-10 mb-10">
                      <el-button size="small" type="danger" @click="triggerPaymentDialog(registration)">
                        Pay Now
                      </el-button>
                    </div>
                  </div>
                </transition>

                <!-- Arrow icon at the bottom -->
                <div class="expand-arrow" @click="toggleExpand(registration)">
                  <i :class="`el-icon-arrow-${registration.expanded ? 'up' : 'down'}`"></i>
                </div>
              </el-card>
            </div>
          </div>
        </el-col>
      </el-row>
    </content-body-card>

    <el-dialog v-if="registration" :show-close="true" :visible="paymentDialog" @close="paymentDialog = false; paymentLoading = false" width="80%" :title="`Payment for ${registration.event[0].name}`">
    <el-row :gutter="20">
      <el-form ref="form" :model="payment" :rules="rules" class="payment-form">
      <el-col :span="12" class="payment-section">
        <h2>Payment Details</h2>
        <el-price-input v-model="payToday" :min="minPayment" :max="outstanding" :key="key" :disabled="registration.payLater" />
        <div class="pay-today">
          Pay Today: <strong>{{ parsePrice(payTodayTotal) }}</strong>
        </div>
        <div class="transaction-fee">*Includes a {{ transactionFee }}% transaction fee</div>
        <div class="credit-card-section">
          <h2>Credit Card Details</h2>
          <credit-card v-model="payment.card" ref="card" :width="300"/>
        </div>
      </el-col>
      <el-col :span="12" class="billing-section">
        <h2>Billing Address</h2>
        <el-form-item label="Address Line 1">
                  <el-input v-model="payment.billingAddress.addressLine1" placeholder="Type here..." />
                </el-form-item>
                <el-form-item label="Address Line 2">
                  <el-input v-model="payment.billingAddress.addressLine2" placeholder="Type here..." />
                </el-form-item>
                <el-form-item label="City/Suburb">
                  <el-input v-model="payment.billingAddress.suburb" placeholder="Type here..." />
                </el-form-item>
                <el-form-item label="State">
                  <el-input v-model="payment.billingAddress.state" placeholder="Type here..." />
                </el-form-item>
                <el-form-item label="Postcode">
                  <el-input v-model="payment.billingAddress.postcode" placeholder="Type here..." />
                </el-form-item>
                <el-form-item class="d-flex flex-column align-items-start" label="Country">
                  <el-select filterable remote reserve-keyword v-model="payment.billingAddress.country" :remote-method="searchCountry" placeholder="Select">
                    <el-option v-for="item in countryItems" :key="item.value" :label="item.label" :value="item.value"></el-option>
                  </el-select>
                </el-form-item>
      </el-col>
      </el-form>
    </el-row>
    <div>
      <el-divider></el-divider>
      <el-row>
        <el-col :span="24">
          <div class="make-payment-btn">
            <el-button type="secondary" @click="paymentDialog = false" :disabled="paymentLoading">Cancel</el-button>
            <el-button type="primary" @click="submitPayment" :loading="paymentLoading" :disabled="paymentLoading">Make Payment</el-button>
          </div>
        </el-col>
      </el-row>
    </div>
    </el-dialog>
    <additional-qs-dialog v-if="showQsDialog" @updateFiles="updateFiles" @updateMeta="updateMeta" @closeDialog="closeDialog" v-model="latestDialogValue" :registration="latestDialogValue.registration" />
  </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import { calculateMemberAge } from '@/utils/helpers';
import { mapGetters } from 'vuex';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts.js';
import { clientNationalIds } from '@/utils/client-rules';
import {
  errormsg, countries, fileUploadAcceptString, resultTypes
} from '@/utils/constants';
import AdditionalQsDialog from './components/AdditionalQsDialog.vue';

const defaultRequiredRule = { required: true, trigger: 'blur', message: errormsg.input_option };
const pin = new Pin.Api(process.env.VUE_APP_PIN_PK, process.env.VUE_APP_PIN_ENV);

export default {
  props: {
    value: Object,
  },
  components: {
    AdditionalQsDialog,
  },
  data() {
    return {
      subTitle:
        'On this screen you can view current team information and registration history.',
      nationalId: +process.env.VUE_APP_NATIONAL_ID,
      key: 0,
      capitalize: _.capitalize,
      registrations: [],
      membershipToRegister: {},
      fileUploadAcceptString,
      get: _.get,
      members: [],
      selectedMember: undefined,
      pin,
      payment: {
        card: {},
        billingAddress: {}
      },
      registration: undefined,
      paymentLoading: false,
      rules: {
        'billingAddress.addressLine1': defaultRequiredRule,
        'billingAddress.suburb': defaultRequiredRule,
        'billingAddress.state': +process.env.VUE_APP_NATIONAL_ID === 17 ? { required: false } : defaultRequiredRule,
        'billingAddress.postcode': defaultRequiredRule,
        'billingAddress.country': defaultRequiredRule,
      },
      payToday: 0,
      countryItems: [],
      countries,
      dialogValue: {
        filtersVisible: false,
        entityType: undefined,
        registration: undefined,
        regoDetails: undefined,
        member: undefined,
      },
      showQsDialog: false,
      paymentDialog: false,
      viewMode: 'list',
      sortBy: 'reg-desc',
      filters: {
        entityType: '',
        paymentStatus: '',
      },
      searchQuery: '',
      breakpoint: 'desktop',
      mobileFilters: {
        entityType: '',
        paymentStatus: '',
      },
      seasonExpanded: {}, // Track which seasons are expanded
    };
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.handleResize();

    // Rest of the existing mounted method
    const { regoId, regoProductId } = this.$route.params;
    if (this.userId) {
      this.$store.commit('loading', { loading: true, text: 'Loading...' });
      this.$http.get(`/assemble/api/v1/portal/team-registrations/members/${this.userId}`).then((resTeam) => {
        const teamRegistrations = _.map(resTeam.data.data, (data) => ({ ...data, results: _.get(data, 'results.0', {}) }));
        this.registrations = teamRegistrations.map((rego) => _.assign(rego, { upgradingDropdown: false }));

        // Set all seasons to be expanded by default
        const seasons = [...new Set(this.registrations.map((r) => r.season))];
        seasons.forEach((season) => {
          this.$set(this.seasonExpanded, season, true);
        });

        // get all members from registrations
        // get all members from registrations with unique IDs
        const teamRegosHasMembers = _.filter(this.registrations, (rego) => rego.member);

        // Use a Map to ensure uniqueness by member ID
        const memberMap = new Map();
        teamRegosHasMembers.forEach((rego) => {
          if (rego.member && !memberMap.has(rego.member._id)) {
            memberMap.set(rego.member._id, rego.member);
          }
        });

        this.members = Array.from(memberMap.values());
        // add option all members
        this.members.unshift({ _id: undefined, profile: { firstName: 'All', lastName: 'Members' } });
        // eslint-disable-next-line no-restricted-syntax
        for (const membership of this.registrations) {
          if (membership.regoEntity && membership.regoEntity.products && membership.regoEntity.products.upgradeableMembershipList && membership.expired !== true) {
            // eslint-disable-next-line no-restricted-syntax
            for (const um of membership.regoEntity.products.upgradeableMembershipList) {
              if (um._id === regoId) {
                const foundProduct = um.products.find((product) => product._id === regoProductId);
                if (foundProduct) {
                  setTimeout(() => {
                    // eslint-disable-next-line prefer-destructuring
                    const position = (document.getElementsByClassName(`${membership._id}`))[0];
                    position.scrollIntoView({ behavior: 'smooth' });
                  }, 200);
                }
              }
            }
          }
        }

        this.$store.commit('loading', false);
      })
        .catch((e) => {
          this.$message.error(e);
          this.$store.commit('loading', false);
        });
    }
  },
  methods: {
    updateFiles({ files, regoId }) {
      const foundIndex = this.registrations.findIndex((rego) => rego._id === regoId);
      if (foundIndex >= 0) this.registrations[foundIndex].files = files;
    },
    handleCommand(command, row) {
      switch (command) {
        case 'download':
          this.downloadReceipt(row);
          break;
        case 'pay':
          this.triggerPaymentDialog(row);
          break;
        case 'questions':
          this.showAQDialog(row);
          break;
        case 'members':
          this.downloadMemberList(row);
          break;
        default:
          break;
      }
    },
    closeDialog() {
      this.dialogValue = {
        filtersVisible: false,
      };
      this.showQsDialog = false;
      this.showRenewPayment = false;
    },
    closeDialogSI() {
      this.dialogValueSI = {
        filtersVisible: false,
      };
      this.showQsDialogSI = false;
    },
    updateMeta({ meta, regoId }) {
      const foundIndex = this.registrations.findIndex((rego) => rego._id === regoId);
      if (foundIndex >= 0) {
        // Create a copy of the registration
        const updatedRego = { ...this.registrations[foundIndex] };
        // Update the meta property
        updatedRego.meta = meta;
        // Use $set to ensure reactivity
        this.$set(this.registrations, foundIndex, updatedRego);
        // Force component to refresh
        this.$forceUpdate();
      }
    },
    showAQDialog(row) {
      console.log(row, 'row');
      this.dialogValue.filtersVisible = true;
      this.dialogValue.entityType = 'team';
      this.dialogValue.registration = row;
      this.dialogValue.regoDetails = this.registrations;
      this.showQsDialog = true;
    },
    getBase64Logo(logoUrl) {
      const logo = logoUrl || this.logo;
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.setAttribute('crossOrigin', 'anonymous');
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const dataURL = canvas.toDataURL('image/png');
          resolve(dataURL);
        };
        img.onerror = (error) => {
          reject(error);
        };
        img.src = `${ logo }?r=${ Math.floor(Math.random() * 100000)}`;
      });
    },
    getBase64LogoWithTimeout(logoUrl, timeout = 10000) {
      return Promise.race([
        this.getBase64Logo(logoUrl),
        new Promise((resolve, reject) => {
          setTimeout(() => reject(new Error('Image load timeout')), timeout);
        })
      ]);
    },
    async downloadMemberList(row) {
      this.$store.commit('loading', { loading: true, text: 'Generating member report...' });
      try {
        if (pdfMake.vfs === undefined) pdfMake.vfs = pdfFonts.pdfMake.vfs;
        // add filter params
        const eventId = row.regoEntity._id;
        const homeDD = await this.$http.get(`/assemble/api/v1/portal/teams/${row.team._id}/member-list-pdf`, { params: { event: eventId } });
        console.log(homeDD.data.data, 'homeDD');
        const logoUrl = homeDD.data.entityImgUrl;
        console.log(logoUrl, 'logoUrl');

        // Process all images (logo + player photos)
        const images = {};

        // Add main logo
        if (logoUrl) {
          try {
            images.logo = await this.getBase64LogoWithTimeout(logoUrl, 15000);
          } catch (error) {
            console.warn('Failed to load entity logo:', error);
          }
        }

        // Process player photos in table cells
        const { data } = homeDD.data;
        if (data.content) {
          // Collect all image loading tasks
          const imageTasks = [];
          data.content.forEach((contentItem) => {
            if (contentItem.table && contentItem.table.body) {
              contentItem.table.body.forEach((tableRow, rowIndex) => {
                tableRow.forEach((cell, cellIndex) => {
                  if (cell.imageUrl && cell.playerId) {
                    imageTasks.push({
                      contentItem,
                      rowIndex,
                      cellIndex,
                      cell,
                      imageKey: `player_${cell.playerId}`
                    });
                  }
                });
              });
            }
          });

          // Process images in batches to avoid overwhelming the browser
          const batchSize = 8;
          let processedCount = 0;

          // eslint-disable-next-line no-await-in-loop
          for (let i = 0; i < imageTasks.length; i += batchSize) {
            const batch = imageTasks.slice(i, i + batchSize);
            const currentBatchEnd = Math.min(i + batchSize, imageTasks.length);

            this.$store.commit('loading', {
              loading: true,
              text: `Loading player photos (${processedCount + 1}-${currentBatchEnd}/${imageTasks.length})...`
            });

            const batchPromises = batch.map(async (task) => {
              try {
                const base64 = await this.getBase64LogoWithTimeout(task.cell.imageUrl, 10000);
                images[task.imageKey] = base64;
                // Replace imageUrl with image reference
                const updatedCell = {
                  ...task.cell,
                  image: task.imageKey,
                  width: 50
                };
                delete updatedCell.imageUrl;
                delete updatedCell.playerId;
                // eslint-disable-next-line no-param-reassign
                task.contentItem.table.body[task.rowIndex][task.cellIndex] = updatedCell;
                return { success: true, playerId: task.cell.playerId };
              } catch (error) {
                console.error(`Failed to load image for player ${task.cell.playerId}:`, error);
                // Keep cell as text if image fails to load
                const updatedCell = {
                  ...task.cell,
                  text: 'Photo unavailable'
                };
                delete updatedCell.imageUrl;
                delete updatedCell.playerId;
                // eslint-disable-next-line no-param-reassign
                task.contentItem.table.body[task.rowIndex][task.cellIndex] = updatedCell;
                return { success: false, playerId: task.cell.playerId, error };
              }
            });

            // Use Promise.allSettled to handle individual failures gracefully
            // eslint-disable-next-line no-await-in-loop
            await Promise.allSettled(batchPromises);
            processedCount += batch.length;
          }

          this.$store.commit('loading', { loading: true, text: 'Generating PDF...' });
        }

        data.content.unshift('\n');
        // Only add logo if it was successfully loaded
        if (images.logo) {
          data.content.unshift({ image: 'logo', width: [100], alignment: 'center' });
        }
        data.images = images;
        console.log(data, 'processed data');
        const result = pdfMake.createPdf(data).download(`${row.team._id}-member-list.pdf`);
        this.$store.commit('loading', false);
        return result;
      } catch (error) {
        console.error('Error generating member report:', error);
        this.$message.error('Failed to generate member report. Please try again.');
        this.$store.commit('loading', false);
        throw error;
      }
    },
    searchCountry(query) {
      if (query !== '') {
        setTimeout(() => {
          this.countryItems = this.countries.filter(
            (item) => item.label.toLowerCase().indexOf(query.toLowerCase()) > -1
          );
        }, 200);
      } else {
        this.countryItems = [];
      }
    },
    triggerPaymentDialog(registration) {
      console.log(registration, 'registration');
      this.$set(this, 'registration', registration);
      const registrationOutstanding = registration.pricing.event.total - this.getAmountPaid(registration);
      this.payToday = registrationOutstanding;
      this.paymentDialog = true;
      this.paymentLoading = false; // Reset loading state when opening dialog
      this.key++;
    },
    getAmountPaid(registration) {
      return registration.payments ? registration.payments.reduce((acc, current) => (current.amount ? acc + current.amount - current.transactionFee : acc), 0) : 0;
    },
    submitPayment() {
      Promise.all([this.$refs.card && this.$refs.card.validate(), this.$refs.form && this.$refs.form.validate()])
        .then(([cardValid, formValid]) => {
          if (this.requirePayment && (!cardValid || !formValid)) return;

          // issue card token
          const [expiryMonth, expiryYear] = this.payment.card.expiry.replace(/\s/g, '').split('/');

          this.paymentLoading = true;
          this.$store.commit('loading', true);
          this.pin.createCardToken({
            number: this.payment.card.number,
            name: this.payment.card.name,
            expiry_month: expiryMonth,
            expiry_year: expiryYear,
            cvc: this.payment.card.cvc,
            address_line1: this.payment.billingAddress.addressLine1,
            address_line2: this.payment.billingAddress.addressLine2 || '',
            address_city: this.payment.billingAddress.suburb,
            address_state: this.payment.billingAddress.state,
            address_postcode: this.payment.billingAddress.postcode,
            address_country: 'Australia',
          }).then((pinResponse) => {
            // Block International Cards
            // eslint-disable-next-line camelcase, no-unused-vars
            const { token, issuing_country, scheme } = pinResponse;
            // || !_.includes(['master', 'visa'], scheme)
            // eslint-disable-next-line camelcase
            if (!['AU', 'NZ'].includes(issuing_country) && [14, 18].includes(+process.env.VUE_APP_NATIONAL_ID)) {
              this.paymentLoading = false;
              this.$store.commit('loading', false);
              this.$message.error('International cards are not supported.');
              return false;
            }

            if (!token) {
              this.paymentLoading = false;
              this.$store.commit('loading', false);
              this.$message.error('There has been an error, try again later.');
            }
            // register with card token
            this.pay(token);
          }).catch((err) => {
            this.paymentLoading = false;
            this.$store.commit('loading', false);
            const pinError = _.get(err, 'error_description');
            this.$message.error(pinError || 'There has been an error');
          });
        }).catch(() => {});
    },
    pay(token) {
      if (!token) {
        this.paymentLoading = false;
        return false;
      }
      this.$store.commit('loading', true);
      return this.$http.post('/assemble/api/v1/portal/payments/outstanding/team', {
        cardToken: token,
        paymentTotal: this.payTodayTotal,
        paymentInstallment: this.payToday,
        eventId: this.registration.entityId,
        teamId: this.registration.team._id,
        payLater: this.registration.payLater,
      }).then((res) => {
        this.paymentLoading = false;
        this.$message.success('Payment Successful');
        this.paymentDialog = false;
        // assign the res.data.data to the correct index in registrations
        const [rego] = res.data.data;
        const { payments, isPaid } = rego;
        const index = this.registrations.findIndex((reg) => reg._id === this.registration._id);
        console.log(index, 'index');
        this.registration.payments = payments;
        this.registration.isPaid = isPaid;
        this.$set(this.registrations, index, this.registration);
        this.$forceUpdate();
        this.registration = undefined;
        this.$store.commit('loading', false);
      }).catch((err) => {
        this.paymentLoading = false;
        const serverError = _.get(err, 'response.data.message');
        this.$store.commit('loading', false);
        this.$message.error(serverError || 'There has been an error');
      });
    },
    getPaidStatus(rego) {
      const { payments, pricing, charge } = rego;
      if (rego.payAfter) {
        // total paid is amount - fee for each payment
        const totalPaid = _.sumBy(payments, (p) => p.amount - (p.transactionFee || 0));
        const total = _.get(pricing, 'event.total', 0);
        return total > 0 ? `$${(totalPaid / 100).toFixed(2)} / $${(total / 100).toFixed(2)}` : '$0';
      }
      // total paid is amount - fee for each chargew
      const totalPaid = _.sumBy(charge, (c) => c.amount - (c.meta.transactionFee || 0));
      const total = _.get(pricing, 'event.total', 0);
      return total > 0 ? `$${(totalPaid / 100).toFixed(2)} / $${(total / 100).toFixed(2)}` : '$0';
    },
    isPaid(rego) {
      const {
        payments, pricing, charge, isPaid, payLater, meta
      } = rego;
      if (rego.teamId === 300367) {
        console.log('rego', rego.regoEntity?.name, rego, isPaid, payLater);
      }
      if (isPaid || meta.paid) {
        return true;
      }
      if (rego.payAfter) {
        // total paid is amount - fee for each payment
        const totalPaid = _.sumBy(payments, (p) => p.amount - p.transactionFee);
        const total = _.get(pricing, 'event.total', 0);
        if (totalPaid === total) {
          return true;
        }
        return false;
      }
      // total paid is amount - fee for each charge
      const totalPaid = _.sumBy(charge, (c) => c.amount - c.meta.transactionFee - (c.meta.fixedTechFee || 0));
      console.log(totalPaid, 'totalPaid');
      const total = _.get(pricing, 'event.total', 0);
      console.log(total, 'total');
      if (totalPaid === total) {
        return true;
      }
      return false;
    },
    getPayButtonText(rego) {
      if (this.isPaid(rego)) {
        return 'Paid';
      }
      return 'Pay';
    },
    isUpgradeEligible(rego) {
      if (rego.entityType === 'membership' && _.get(rego, 'regoEntity.products.isUpgradeable') && !rego.upgradingDropdown && !rego.expired) {
        const { dob, gender } = this.value.profile;
        const endOfYear = this.$store.getters['national/endOfYear'];
        const age = calculateMemberAge(dob, endOfYear);
        const membershipList = _.get(rego, 'regoEntity.products.upgradeableMembershipList', []);
        const checkEligible = membershipList.some((membership) => _.get(membership, 'products', []).some((product) => age >= product.ageFrom && age <= product.ageTo && (product.gender === gender || product.gender === 'All' || (product.gender === 'Other' && (gender !== 'Male' || gender !== 'Female')))));
        return checkEligible;
      }
      return false;
    },
    getFilterdMemberships(upgradeableMembershipList) {
      const { dob, gender } = this.value.profile;
      const endOfYear = this.$store.getters['national/endOfYear'];
      const age = calculateMemberAge(dob, endOfYear);
      const list = _.cloneDeep(upgradeableMembershipList);
      list.forEach((membership) => {
        // eslint-disable-next-line no-param-reassign
        membership.products = membership.products.filter((product) => age >= product.ageFrom && age <= product.ageTo && (product.gender === gender || product.gender === 'All' || (product.gender === 'Other' && (gender !== 'Male' || gender !== 'Female'))));
      });
      return list;
    },
    parsePrice(val) {
      const rs = (val / 100).toFixed(2);
      if (val < 0) return `-$${(Math.abs(rs)).toFixed(2)}`;
      return `$${rs}`;
    },
    async downloadReceipt(registration) {
      const parsePrice = (val) => (val / 100).toFixed(2);
      let charges = [];
      try {
        const response = await this.$http.get(`/assemble/api/v1/portal/team-registrations/${registration._id}/receipt`);
        charges = response.data.data;
      } catch {
        this.$message.error('An error occured while downloading receipt');
      }

      if (pdfMake.vfs === undefined) {
        pdfMake.vfs = pdfFonts.pdfMake.vfs;
      }

      if (!charges.length) this.$message.success('This registration did not incur any cost thus no receipt was found');

      charges.forEach((charge) => {
        const {
          _id: chargeId, registrations, amount, meta, createdAt
        } = charge;

        const { regoEntity, event } = registrations[0];
        const product = regoEntity?.products || event?.products;

        const totalRegistrationFee = _.sumBy(registrations, (r) => _.get(r, `pricing.${r.entityType}.price`, 0));
        const totalSaleableItems = _.sumBy(registrations, (r) => _.get(r, `pricing.${r.entityType}.totalSaleableItems`, 0));
        const discount = _.sumBy(registrations,
          (r) => _.get(r, `pricing.${r.entityType}.discounts[0].redeemedAmount`, 0)
            + _.get(r, 'pricing.activeKidsVoucher.redeemedAmount', 0));

        const bodySummary = [
          [
            { text: 'Registration Fee', style: 'bold' },
            { text: `$${parsePrice(totalRegistrationFee)}`, style: ['alignRight', 'bold'] },
          ],
          [
            { text: 'Saleable Items', style: 'bold' },
            { text: `$${parsePrice(totalSaleableItems)}`, style: ['alignRight', 'bold'] },
          ],
          [
            { text: 'Discounts', style: 'bold' },
            { text: `-$${parsePrice(discount)}`, style: ['alignRight', 'bold'] },
          ],
          [
            { text: 'Transaction Fee', style: 'bold' },
            { text: `$${parsePrice(meta.transactionFee || 0)}`, style: ['alignRight', 'bold'] },
          ],
          [
            { text: 'GST Incl.', style: 'bold' },
            { text: `$${parsePrice(amount / 11)}`, style: ['alignRight', 'bold'] },
          ],
          [
            { text: 'Amount Paid', style: 'bold' },
            { text: `$${parsePrice(amount)}`, style: ['alignRight', 'bold'] },
          ]
        ];

        // Add Fixed Tech Fee if it exists in the charge metadata
        if (meta.fixedTechFee && meta.fixedTechFee > 0) {
          bodySummary.splice(4, 0, [
            { text: 'Fixed Technology Fee', style: 'bold' },
            { text: `$${parsePrice(meta.fixedTechFee)}`, style: ['alignRight', 'bold'] },
          ]);
        }

        // Adjust GST removal index if fixed tech fee was added
        const gstIndex = (meta.fixedTechFee && meta.fixedTechFee > 0) ? 5 : 4;
        if (_.get(product, 'gstRegistered') === false) bodySummary.splice(gstIndex, 1);

        const dd = {
          content: [
            { image: 'logo', width: [100], alignment: 'center' },
            {
              text: `You have successfully made a payment of $${parsePrice(amount)} AUD.`,
              margin: [0, 25, 0, 0],
            },
            '\n',
            `Invoice ID: ${chargeId}`,
            moment(createdAt).format('DD/MM/YYYY'),
            '\n',
            {
              text: 'Payment breakdown:',
              style: {
                bold: true
              }
            },
            ...registrations.map((rego) => ([
              {
                text: _.get(rego, 'entity.name', ''), style: 'subheader'
              },
              {
                style: 'tableExample',
                table: {
                  widths: ['auto', '*'],
                  textAlign: 'center',
                  body: [
                    [
                      { text: 'Team ID', style: 'bold' },
                      { text: rego.teamId, style: ['alignLeft', 'bold'] },
                    ],
                    [
                      { text: 'Team Name', style: 'bold' },
                      { text: rego.team.name, style: ['alignLeft', 'bold'] },
                    ],
                    [
                      { text: 'Team Contact Name', style: 'bold' },
                      { text: rego.team.contact.name, style: ['alignLeft', 'bold'] },
                    ],
                    [
                      { text: 'Team Contact Mobile', style: 'bold' },
                      { text: rego.team.contact.number, style: ['alignLeft', 'bold'] },
                    ],
                    [
                      { text: 'Team Contact Email', style: 'bold' },
                      { text: rego.team.contact.email, link: rego.team.contact.email, style: ['alignLeft', 'bold'] },
                    ],
                    [
                      { text: 'Team Registration Link', style: 'bold' },
                      {
                        text: `${window.location.origin}/finder?id=${rego.teamId}&entityId=${rego.entityId}${rego.team.teamCode ? `&teamCode=${rego.team.teamCode}` : ''}`,
                        link: `${window.location.origin}/finder?id=${rego.teamId}&entityId=${rego.entityId}${rego.team.teamCode ? `&teamCode=${rego.team.teamCode}` : ''}`,
                        style: ['alignLeft', 'bold']
                      },
                    ],
                    ...(rego.team.teamCode ? [
                      [
                        { text: 'Team Code', style: 'bold' },
                        { text: rego.team.teamCode, style: ['alignLeft', 'bold'] },
                      ]
                    ] : [])
                  ],
                },
                layout: {
                  hLineColor: 'lightgray',
                  vLineColor: 'lightgray',
                }
              },
              {
                style: 'tableExample',
                table: {
                  widths: ['auto', '*', '*'],
                  textAlign: 'center',
                  body: [
                    [
                      { text: 'Product', style: 'tableHeader' },
                      { text: 'Qty', style: ['tableHeader', 'alignRight'] },
                      { text: 'Cost', style: ['tableHeader', 'alignRight'] },
                    ],
                    [
                      { text: rego.productName || 'Registration Fee' },
                      { text: '1', style: 'alignRight' },
                      { text: `$${parsePrice(rego.pricing[rego.entityType].price)}`, style: 'alignRight' },
                    ],
                    ...rego.pricing[rego.entityType].saleableItems.map((item) => ([
                      { text: item.name },
                      { text: item.qty, style: 'alignRight' },
                      { text: `$${parsePrice(item.price)}`, style: 'alignRight' },
                    ])),
                    ...rego.pricing[rego.entityType].discounts.map((d) => ([
                      { text: d.name },
                      '',
                      { text: `$${parsePrice(d.redeemedAmount)}`, style: 'alignRight' },
                    ])),
                    rego.pricing.activeKidsVoucher
                      ? ([
                        { text: 'Active Kids Voucher' },
                        { text: '' },
                        { text: `$${parsePrice(rego.pricing.activeKidsVoucher.redeemedAmount)}`, style: 'alignRight' },
                      ])
                      : ['', '', ''],
                  ]
                },
                layout: {
                  hLineColor: '#ffffff',
                  vLineColor: '#ffffff',
                }
              },
            ])),
            'Payment Summary:\n',
            {
              style: 'tableExample',
              table: {
                widths: ['auto', '*'],
                textAlign: 'center',
                body: bodySummary,
              },
              layout: {
                hLineColor: 'lightgray',
                vLineColor: 'lightgray'
              }
            },
          ],
          styles: {
            header: {
              fontSize: 18,
              bold: true,
              margin: [0, 0, 0, 10]
            },
            subheader: {
              fontSize: 14,
              bold: true,
              color: '#111111',
              margin: [0, 10, 0, 5]
            },
            tableExample: {
              margin: [0, 20, 0, 20],
            },
            tableHeader: {
              bold: true,
              fontSize: 13,
              color: 'black',
              fillColor: 'lightgray',
            },
            alignRight: {
              alignment: 'right',
            },
            bold: {
              bold: true,
              color: '#111111',
            }
          },
          images: {
            logo: 'https://assemble-sports-assets.s3-ap-southeast-2.amazonaws.com/Assemble+Logo_RGB_Positive.png'
          },
          defaultStyle: {
            fontSize: 14,
          }
        };
        pdfMake.createPdf(dd).download(`${this.userId}-${chargeId}.pdf`);
      });
    },
    getResultLabel: (type) => _.find(resultTypes, { type }).label,
    beforeUpload(file) {
      const size = file.size / 1024 / 1024;
      if (size > 10) {
        this.$message.error('File size is more than 10mb');
        return false;
      }
      return true;
    },
    onError(error) {
      if (error.message) {
        const json = JSON.parse(error.message);
        this.$message.error(json.message || 'There has been an error');
      } else {
        this.$message.error('There has been an error');
      }
    },
    entityFormatter(row) {
      if (!row.fromImport) {
        if (row.team && row.team.name) return `${row.team.name} - ${_.get(row, 'regoEntity.name')}`;
        return row.regoEntity.name || '';
      }

      return row.meta.productName || '';
    },
    generateTeamCode(registration) {
      this.$store.commit('loading', { loading: true, text: 'Loading...' });
      this.$http.post(`/assemble/api/v1/portal/team/${registration.team._id}/generate-team-code`)
        .then((res) => {
          const code = res.data.data;
          console.log('code', code);
          const index = this.registrations.findIndex((r) => r._id === registration._id);
          this.registrations[index].team.teamCode = code;
          this.$message.success('Team code generated successfully');
          this.$store.commit('loading', false);
          this.$forceUpdate();
        })
        .catch((e) => {
          this.$message.error(e);
          this.$store.commit('loading', false);
        });
    },
    dateFormatter(timestamp) {
      return this.moment(timestamp).format('DD/MM/YYYY HH:mm');
    },
    handleChangeMem(rego) {
      const registrations = this.$store.getters['cart/registrations'];
      const [id, productId] = _.get(rego, 'regoEntity.membershipToUpgrade');
      const memberId = this.value._id;
      this.$confirm('Are you sure you want to upgrade this membership?', 'Confirm', {
        distinguishCancelAndClose: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
      })
        .then(() => {
          this.$store.commit('loading', { loading: true, text: 'Loading...' });
          this.$http.post(`/assemble/api/v1/portal/registrations/search/entities/${id}/products/${productId}`)
            .then((res) => {
              const { result, transactionFee } = res.data.data;
              const [resutObject] = result;
              // eslint-disable-next-line no-param-reassign
              this.membershipToRegister = resutObject;
              const { _id: entityId, productType, memberType } = this.membershipToRegister;

              const foundRego = _.find(registrations, (r) => r.entityId === entityId
                                && r.memberId === memberId
                                && r.productType === productType
                                && r.memberType === memberType);
              if (foundRego) this.$message.error('Registration for this member has already been added to cart.');
              else {
                this.$store.commit('cart/setTransactionFee', transactionFee);
                // eslint-disable-next-line no-param-reassign
                delete rego.upgradingDropdown;
                this.$router.push({
                  name: 'membership-register-individual',
                  params: { memberId, selectedMembership: this.membershipToRegister, upgradeFromMembership: rego },
                });
              }
              this.$store.commit('loading', false);
            }).catch((e) => {
              this.$message.error(e.response.data.message);
              this.$store.commit('loading', false);
            });
        })
        .catch(() => {});
    },
    async handleChange(file, fileList, config) {
      const fileIndex = fileList.findIndex((fl) => fl.name === file.name);
      try {
        this.$store.commit('loading', { loading: true, text: 'Loading...' });
        const res = await this.$http.post('/assemble/api/v1/portal/files/presigned-url', { fileName: file.name });
        const { url, name } = res.data.data;
        await fetch(url, {
          method: 'PUT',
          body: file.raw,
          headers: { 'Content-Type': '*/*' },
        });
        const nameOnly = name.split('.')[0];
        const fileTypeOnly = name.split('.')[name.split('.').length - 1];
        // eslint-disable-next-line no-param-reassign
        fileList[fileIndex].name = nameOnly;
        // eslint-disable-next-line no-param-reassign
        fileList[fileIndex].type = fileTypeOnly;
        this.$store.commit('loading', false);
      } catch (e) {
        this.$message.error('There has been an error');
      }
      this.$store.commit('loading', { loading: true, text: 'Loading...' });

      // After uploading via s3 presigned url, update file-uploads collection
      this.$http.post(this.fileUpdateUrl, {
        originalName: file.raw.name.split('.')[0],
        fileName: file.name,
        fileType: file.type,
        ...config
      }).then((res) => {
        const { fileId, fileName } = res.data.data;
        this.$message.success('File uploaded successfully');
        // eslint-disable-next-line no-param-reassign
        fileList[fileIndex]._id = fileId;
        // eslint-disable-next-line no-param-reassign
        fileList[fileIndex].name = `${fileName} Uploaded At: ${this.dateFormatter(this.moment())}`;
      }).catch((e) => {
        console.log(e);
        this.$message.error('There has been an error uploading the file');
      }).finally(() => {
        this.$store.commit('loading', false);
      });
    },
    removeFile(file) {
      const fileId = file._id;
      if (fileId) {
        return this.$confirm('You are about to permanently delete this file. Are you sure you want to continue?', 'Warning', { type: 'warning' })
          .then(() => {
            this.$store.commit('loading', { loading: true, text: 'Loading...' });
            this.$http.delete(`/assemble/api/v1/portal/files/${fileId}`)
              .then(() => {
                this.$message.success('Success!');
                this.$store.commit('loading', false);
              })
              .catch(() => {
                this.$message.error('Error deleting file');
                this.$store.commit('loading', false);
              });
          })
          .catch();
      } if (file) {
        // Catch for when the file hasn't been uploaded yet
        return true;
      }
      return false;
    },
    getUploadExpiry(rego) {
      const { entityType } = rego;
      if (entityType === 'event') {
        const [event] = rego.event;
        const { regoEndDate, isActive } = event;
        if (moment().valueOf() <= regoEndDate && isActive) return false;
        return true;
      }
      return false;
    },
    getUploadTip(rego, field) {
      const question = rego.additionalQs.find((q) => q.fieldName === field);
      return question
        ? question.question
        : 'Uploaded files must be 10mb or less';
    },
    submitUpload(ref) {
      this.$store.commit('loading', { loading: true, text: 'Loading...' });
      const uploadComponent = this.$refs[ref][0];
      uploadComponent.submit();
    },
    previewUpload(file) {
      const { _id } = file;
      this.$http.get(`/assemble/api/v1/portal/files/${_id}/signed`)
        .then((res) => {
          window.open(res.data.url, '_blank');
        })
        .catch((err) => {
          this.$message.error(err);
        });
    },
    groupedUploads(data) {
      const grouped = _.groupBy(data, (file) => file.meta.additionalQuestion);
      const out = Object.keys(grouped).map((group) => ([group, grouped[group]]));
      return out;
    },
    isNotExpired(rego) {
      if (+this.moment() <= rego.event[0].regoEndDate && rego.event[0].isActive) {
        return true;
      }
      return false;
    },
    handleDropdown(rego, val) {
      if (val === 'open') {
        // eslint-disable-next-line no-param-reassign
        rego.upgradingDropdown = true;
      } else {
        // eslint-disable-next-line no-param-reassign
        rego.upgradingDropdown = false;
      }
      this.key++;
    },
    getClassnameById({ row }) {
      return `${row._id}`;
    },
    joinTag(vals) {
      return vals ? _.join(_.map(vals, (v) => v.name), ', ') : '';
    },
    handleResize() {
      if (window.innerWidth < 992) {
        this.breakpoint = 'mobile';
      } else {
        this.breakpoint = 'desktop';
      }
    },
    toggleSeason(season) {
      this.$set(this.seasonExpanded, season, !this.seasonExpanded[season]);
    },
    toggleExpand(registration) {
      // Toggle the expanded state
      this.$set(registration, 'expanded', !registration.expanded);
    },
    getRowClass({ row }) {
      if (row.isPaid) {
        return 'paid-row';
      }
      return 'unpaid-row';
    },
    beforeEnter(el) {
      // eslint-disable-next-line no-param-reassign
      el.style.height = '0';
    },
    enter(el) {
      // This ensures the element is displayed during the transition
      // eslint-disable-next-line no-param-reassign
      el.style.display = 'block';
      // eslint-disable-next-line no-param-reassign
      el.style.overflow = 'hidden';
      // eslint-disable-next-line no-param-reassign
      el.style.height = `${el.scrollHeight}px`;
    },
    beforeLeave(el) {
      // eslint-disable-next-line no-param-reassign
      el.style.height = `${el.scrollHeight}px`;
    },
    leave(el) {
      // Force a reflow, necessary to restart the animation
      // eslint-disable-next-line no-void
      void el.offsetHeight;
      // eslint-disable-next-line no-param-reassign
      el.style.height = '0';
    },
    getValue(obj, path, defaultValue = '-') {
      return this.get(obj, path, defaultValue);
    },
    eventNameFormatter(row) {
      if (!row || !row.regoEntity) return '';

      if (!row.fromImport) {
        return _.get(row, 'regoEntity.name', '');
      }
      return row.meta && row.meta.productName ? row.meta.productName : '';
    },
  },
  computed: {
    latestDialogValue() {
      return this.dialogValue;
    },
    logo() {
      const logo = process.env.VUE_APP_LOGO && process.env.VUE_APP_LOGO !== '' ? process.env.VUE_APP_LOGO : 'https://assemble-sports-assets.s3-ap-southeast-2.amazonaws.com/Assemble+Logo_RGB_Positive.png';
      return logo;
    },
    transactionFee() {
      let defaultFee = +this.$store.getters['cart/transactionFee'];
      if (!defaultFee) {
        const nationalMeta = this.$store.getters['national/getMeta'];
        if (nationalMeta && nationalMeta.transactionFee) {
          defaultFee = +nationalMeta.transactionFee;
        }
      }
      const preferredFee = +this.$store.getters['national/paymentPreference'].addToTotal;
      console.log(preferredFee, 'preferredFee');
      return !(preferredFee >= 0) ? defaultFee : preferredFee;
    },
    filteredRegistrations() {
      return this.registrations.filter((rego) => {
        // Check member filter
        const memberMatch = !this.selectedMember || rego.memberId === this.selectedMember;

        // Check team name search
        const searchMatch = !this.searchQuery
          || (rego.team && rego.team.name
           && rego.team.name.toLowerCase().includes(this.searchQuery.toLowerCase()));

        // Check payment status filter
        let paymentMatch = true;
        if (this.filters.paymentStatus === 'paid') {
          paymentMatch = this.isPaid(rego);
        } else if (this.filters.paymentStatus === 'unpaid') {
          paymentMatch = !this.isPaid(rego);
        }

        return memberMatch && searchMatch && paymentMatch;
      });
    },
    customMemberDetailForm() {
      const nationalId = _.includes(clientNationalIds, +process.env.VUE_APP_NATIONAL_ID) ? process.env.VUE_APP_NATIONAL_ID : 0;
      // eslint-disable-next-line
      return () => import('@/views/app/clients/' + nationalId + '-member-profile-extras.vue');
    },
    accept() {
      return fileUploadAcceptString;
    },
    isNSWISA() {
      return +process.env.VUE_APP_NATIONAL_ID === 1;
    },
    memberIdLabel() {
      return +process.env.VUE_APP_NATIONAL_ID === 1 ? 'NSWISA Member ID' : 'Member ID';
    },
    fileUpdateUrl() {
      return `${process.env.VUE_APP_API_URL}/assemble/api/v1/portal/files/registration`;
    },
    ...mapGetters('user', [
      'uploadHeaders',
    ]),
    userId() {
      return this.$store.getters['user/userId'];
    },
    totalPrice() {
      const { registrations } = this;
      if (registrations && registrations.length > 0) {
        return registrations.reduce((acc, registration) => {
          const { pricing } = registration;
          const { total } = pricing.event;
          console.log(total, 'total');
          return acc + total;
        }, 0);
      }
      return 0;
    },
    amountPaidWithFee() {
      if (this.registration && this.registration.payments) {
        return _.sumBy(this.registration.payments, 'amount');
      }
      return 0;
    },
    amountPaid() {
      const { registration } = this;
      if (registration) {
        return this.getAmountPaid(registration);
      }
      return 0;
    },
    outstanding() {
      const { registration } = this;
      if (registration) {
        const { total } = registration.pricing.event;
        const amountPaid = this.getAmountPaid(registration);
        return total - amountPaid;
      }
      return 0;
    },
    minPayment() {
      const { outstanding } = this;
      return outstanding > 100 ? 100 : outstanding;
    },
    payTodayTotal() {
      const { payToday } = this;
      console.log(payToday, 'payToday');
      return +(payToday + ((payToday * this.transactionFee) / 100)).toFixed() || 0;
    },
    registrationsBySeason() {
      // Group registrations by season
      const grouped = _.groupBy(this.filteredRegistrations, 'season');

      // Convert to array of {season, registrations} objects, sorted by season desc
      return Object.entries(grouped)
        .map(([season, registrations]) => ({
          season,
          registrations,
          isExpanded: this.seasonExpanded[season] !== undefined ? this.seasonExpanded[season] : true
        }))
        .sort((a, b) => b.season.localeCompare(a.season)); // Sort seasons in descending order
    },
    filteredMobileRegistrations() {
      return this.registrations.filter((registration) => {
        // Check member filter
        const memberMatch = !this.selectedMember || registration.memberId === this.selectedMember;

        // Check entity type filter
        const entityTypeMatch = !this.mobileFilters.entityType || registration.entityType === this.mobileFilters.entityType;

        // Check payment status filter
        let paymentMatch = true;
        if (this.mobileFilters.paymentStatus === 'paid') {
          paymentMatch = this.isPaid(registration);
        } else if (this.mobileFilters.paymentStatus === 'unpaid') {
          paymentMatch = !this.isPaid(registration);
        }

        return memberMatch && entityTypeMatch && paymentMatch;
      });
    },
  },
  filters: {
    cap(value) {
      return _.capitalize(value);
    },
    formatDate(date) {
      return moment(date).format('DD/MM/YYYY');
    },
  }
};
</script>

<style lang="scss" scoped>
::v-deep .el-card__body{
  padding: 0;
}
::v-deep .el-card{
  border: none;
}

 ::v-deep .el-dialog{
      max-width: 70rem !important;
    }
    ::v-deep .el-dialog__body{
      display: inline-block;
    }
::v-deep .item-table {
  border: 1px solid rgba(45, 58, 69, 0.1);

  th {
    background: #f6f8fa !important;
    padding-right: 1em;
    text-align: left;
  }

  td {
    background-color: white !important;
    padding-top: 2em;
    text-align: left;
  }
}
  .payment-section, .billing-section {
    background-color: #fff;
    margin-bottom: 20px;
    h2 {
      color: #333;
      font-weight: bold;
      margin-bottom: 15px;
    }
    .input-with-prefix {
      .el-input__inner {
        padding-left: 35px;
      }
      .dollar-sign {
        position: absolute;
        left: 10px;
        top: 50%;
        transform: translateY(-50%);
        color: #333;
        font-weight: bold;
      }
    }
    .pay-today {
      font-size: 16px;
      margin-top: 1rem;
      margin-bottom: 1rem;
      strong {
        color: #4CAF50;
        font-weight: bold;
      }
    }
    .transaction-fee {
      color: #999;
      font-size: 14px;
      margin-bottom: 20px;
    }
    .credit-card-section {
      margin-top: 20px;
    }
  }
  .billing-section {
    h2 {
      margin-top: 0;
    }
  }
  .make-payment-btn {
    display: flex;
    justify-content: center;
  }

.price-divider {
  padding: 0 0.5rem;
}

.operations-buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;

  .el-button {
    padding: 7px;
    margin: 0;
  }
}

// New styles for card and list view

// Common styles
.w-100 {
  width: 100%;
}

.d-flex {
  display: flex;
}

.justify-content-between {
  justify-content: space-between;
}

.align-items-center {
  align-items: center;
}

.align-items-start {
  align-items: flex-start;
}

.flex-column {
  flex-direction: column;
}

.text-center {
  text-align: center;
}

.text-right {
  text-align: right;
}

.mb-05 {
  margin-bottom: 0.5rem;
}

.mb-10 {
  margin-bottom: 10px;
}

.mb-15 {
  margin-bottom: 15px;
}

.mb-20 {
  margin-bottom: 20px;
}

.mt-05 {
  margin-top: 5px;
}

.mt-10 {
  margin-top: 10px;
}

.ml-5 {
  margin-left: 5px;
}

.break-word {
  word-break: break-word;
}

.gap-10 {
  gap: 10px;
}

// Card grid layout
.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(450px, 1fr));
  gap: 12px;
}

::v-deep(.el-card__header) {
  padding: 0;

  @media (max-width: 768px) {
    padding: 0 !important;
  }
}

// Registration card styles
.registration-card {
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03) !important;
  transition: all 0.2s ease;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 0 !important;
  border: 1px solid #EBEEF5;
}

.registration-card:hover {
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05) !important;
}

.card-header {
  padding: 14px 16px;
  border-bottom: 1px solid #EBEEF5;
  height: 100px; /* Fixed height instead of min-height */
  display: flex;
  align-items: flex-start; /* Align to top */
  background-color: #f9f9f9;
  overflow: hidden; /* Hide overflow content */
}

.card-content {
  padding: 12px 16px;
  background-color: #FFFFFF;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  margin-bottom: 0 !important;
}

.card-body-row {
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 10px;
  margin-bottom: 10px;
}

.entity-name {
  font-size: 17px;
  font-weight: 600;
  color: #2c3e50;
  margin-right: 10px;
  line-height: 1.3;
  word-wrap: break-word;
  margin-bottom: 8px;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* Limit to 2 lines */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 44px; /* Approximately 2 lines of text */
}

.team-name-text {
  font-size: 14px;
  color: #333;
  margin-top: 0;
  margin-bottom: 4px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.payment-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  margin-left: 5px;

  &.paid {
    background-color: #F0F9EB;
    color: #67C23A;
    border: 1px solid #E1F3D8;
  }

  &.unpaid {
    background-color: #FEF0F0;
    color: #F56C6C;
    border: 1px solid #FDE2E2;
  }
}

// Info section for card content
.info-section {
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
}

.info-section h4 {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #303133;
  border-bottom: 1px solid #ebeef5;
  padding-bottom: 5px;
}

.entity-type-text {
  font-size: 13px;
  color: #606266;
  text-transform: capitalize;
  font-weight: 500;
  margin-bottom: 4px;
}

.entity-type-mobile {
  font-weight: 600;
  text-transform: capitalize;
}

// Unified info grid styles
.unified-info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  width: 100%;
  padding: 16px 12px 25px;
  background-color: #FAFBFC;
  border-radius: 4px;
  margin-bottom: 0;
  min-height: 80px;
  position: relative;
}

.unified-info-grid .info-col {
  position: relative;
  padding: 0 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.unified-info-grid .info-col:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 5px;
  bottom: 5px;
  right: 0;
  width: 1px;
  background-color: #eaedf2;
}

.info-col .info-label {
  color: #909399;
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
}

.info-col .info-label i {
  margin-right: 5px;
  font-size: 14px;
}

.info-col .info-value {
  color: #2c3e50;
  font-size: 15px;
  font-weight: 500;
}

// Expand button
.expand-button-container {
  position: absolute;
  bottom: -16px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 2;
}

.expand-button-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #606266;
  font-size: 16px;
  padding: 0;
  margin: 0;
  height: 32px;
  width: 32px;
  background-color: white;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  border: 1px solid #EBEEF5;
}

.arrow-icon {
  transition: transform 0.3s ease;
}

// Expanded content
.expanded-content {
  padding: 20px 15px 10px;
  background-color: #fcfcfc;
  border-top: 1px solid #f0f0f0;
}

// Action sections
.action-section {
  margin-top: 10px;
  padding: 10px 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
}

.action-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.payment-action-buttons {
  display: flex;
  gap: 8px;
  padding-right: 15px;
  margin-right: 15px;
  position: relative;
}

.pay-button {
  font-weight: 600;
  background-color: #F56C6C;
  border-color: #F56C6C;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(245, 108, 108, 0.15);
}

.pay-button:hover {
  background-color: #dd6161;
  border-color: #dd6161;
  transform: translateY(-1px);
  box-shadow: 0 2px 3px rgba(245, 108, 108, 0.2);
}

// Upgrade dropdown
.upgrade-dropdown {
  background-color: #f9f9f9;
  border-radius: 4px;
  padding: 10px;
}

// Transitions
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  max-height: 1000px;
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  max-height: 0;
  overflow: hidden;
}

// Filters container styling
.filters-container {
  background-color: #f9f9f9;
  padding: 15px;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 15px;
}

// Season grouping styles
.season-group {
  margin-bottom: 25px;
}

.season-header {
  background-color: #F2F6FC;
  border-radius: 4px;
  padding: 12px 15px;
  margin-bottom: 15px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #EBEEF5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
}

.season-header:hover {
  background-color: #E6F1FC;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
}

.season-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
}

.season-title i {
  margin-right: 8px;
  transition: transform 0.2s;
}

.season-count {
  font-size: 13px;
  color: #909399;
}

.season-content {
  padding: 0 10px;
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

// List view styles
.list-view .season-group {
  margin-bottom: 30px;
}

.list-view .season-header {
  background-color: #f0f7ff;
  border-radius: 8px 8px 0 0;
  padding: 14px 18px;
  margin-bottom: 0;
  border-bottom: none;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
  position: relative;
  z-index: 2;
}

.list-view .list-content {
  border-radius: 0 0 8px 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  overflow: hidden;
}

// Enhanced table styling
::v-deep(.list-view .el-table) {
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  border: 1px solid #ebeef5;
  width: 100% !important;
  table-layout: fixed !important;
}

// Clean table styling
.clean-table {
  border: 1px solid #EBEEF5 !important;
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03) !important;
}

::v-deep(.clean-table th) {
  background-color: #F5F7FA !important;
  font-weight: 500 !important;
  color: #606266 !important;
  border-bottom: 1px solid #EBEEF5 !important;
  padding: 10px 0 !important;
}

::v-deep(.clean-table td) {
  border-bottom: 1px solid #EBEEF5 !important;
  padding: 14px 0 !important;
}

// Table expand content styling
.table-expand-content {
  padding: 10px 20px;
  background-color: #FAFBFC;
}

.expanded-tabs {
  margin-top: 10px;
  border-radius: 4px;
  overflow: hidden;
}

::v-deep(.el-table__expanded-cell) {
  padding: 15px 50px !important;
  background-color: #FAFBFC;
}

.info-detail {
  margin-bottom: 8px;
  display: flex;
  align-items: flex-start;
}

.info-detail strong {
  min-width: 120px;
  display: inline-block;
  font-weight: 500;
  color: #606266;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-label {
  font-weight: 500;
  color: #909399;
  min-width: 120px;
}

.expanded-actions, .expanded-upgrade {
  margin-top: 15px;
  display: flex;
  gap: 10px;
  border-top: 1px solid #EBEEF5;
  padding-top: 15px;
}

.list-value {
  font-size: 14px;
  color: #303133;
  padding: 0 5px;
  word-break: break-word;
  word-wrap: break-word;
  white-space: normal;
  text-align: center;
  display: block;
}

.entity-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 0 5px;
  text-align: left !important;
}

.entity-name-value {
  font-weight: normal;
  font-size: 14px;
  color: #2c3e50;
  white-space: normal;
  overflow: visible;
  text-overflow: initial;
  max-width: none;
  display: block;
  word-break: break-word;
  word-wrap: break-word;
  cursor: default;
  line-height: 1.4;
  text-align: left !important;
}

.payment-actions-container {
  display: flex;
  margin-top: 5px;
  gap: 5px;
}

.status-tag {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  line-height: 22px;
  padding: 0 10px;
  font-size: 12px;
  white-space: nowrap;
  border-radius: 4px;
}

.pay-button-list {
  font-weight: 600;
  background-color: #F56C6C;
  border-color: #F56C6C;
  transition: all 0.2s ease;
  white-space: nowrap;
  padding: 5px 10px;
  font-size: 12px;
}

.pay-button-list:hover {
  background-color: #dd6161;
  border-color: #dd6161;
  transform: translateY(-1px);
}

.action-icon {
  font-size: 20px;
  color: #909399;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: all 0.2s;
}

.action-icon:hover {
  color: #409EFF;
  background-color: #ecf5ff;
}

.list-actions {
  display: flex;
  justify-content: center;
  align-items: center;
}

// Mobile view styles
.box-card {
  border-radius: 4px !important;
  overflow: hidden;

  &.highlight-card {
    border-left-color: #E6A23C;
  }

  .header-container {
    background: linear-gradient(to right, #f6f8fa, white);
    padding: 0;
    border-bottom: 1px solid #eee;

    .d-flex {
      padding: 6px 10px;
    }
  }
}

.d-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 8px;
  padding: 0 12px 10px;
}

.grid-cols-2 {
  grid-template-columns: 1fr 1fr;
}

.grid-item {
  min-width: 0;
}

.grid-item i {
  margin-right: 4px;
  color: #909399;
}

.expand-arrow {
  text-align: center;
  padding: 5px 0;
  color: #909399;
  cursor: pointer;
  margin-top: 10px;
  border-top: 1px solid #f0f0f0;

  &:hover {
    color: #409EFF;
  }
}

.expandable-content {
  padding: 10px;
}

.team-code-mobile {
  font-weight: 500;
}

.event-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  padding: 8px 0;
}

.event-name {
  font-weight: 500;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.team-code-list {
  font-size: 12px;
  color: #909399;
}

.table-expand-content {
  padding: 20px;
}

.expanded-tabs {
  margin-bottom: 15px;
}

.expanded-details {
  padding: 10px 0;
}

.expanded-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 15px;
}

.info-section {
  margin-bottom: 20px;
}

.info-section h4 {
  margin-bottom: 10px;
  color: #303133;
  font-size: 16px;
  font-weight: 500;
}

.info-detail {
  margin-bottom: 5px;
  font-size: 14px;
}

.status-details {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 5px;
}

.status-tag {
  margin-left: 5px;
}

.mt-5 {
  margin-top: 5px;
}

.payment-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.payment-status {
  font-size: 14px;
  color: #303133;
  margin-bottom: 2px;
}

.team-name-text {
  font-size: 14px;
  color: #333;
  margin-top: 4px;
  margin-bottom: 4px;
  font-weight: 500;
}

.generate-code-btn {
  font-size: 13px;
  color: #409EFF;
  padding: 0;
  margin-top: 2px;
  margin-bottom: 2px;

  &:hover {
    color: #66b1ff;
    text-decoration: underline;
  }
}
</style>
